import { MEDIA, BUSINESS } from "@/data";
import { CallButton } from "./Buttons";
import * as Icons from "lucide-react";

/**
 * MeetPlugMan — dedicated mascot showcase section.
 * Houses the additional Plug-Man photography (counter scene, pointing, portrait)
 * without swapping out any of the existing imagery elsewhere on the site.
 */
export default function MeetPlugMan() {
  return (
    <section
      id="plug-man"
      data-testid="plug-man-section"
      className="relative bg-white py-20 md:py-28 px-4 md:px-8 overflow-hidden"
    >
      {/* Soft brand wash */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-white to-orange-50/50" aria-hidden />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-red/10 blur-[120px]" aria-hidden />
      <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-brand-blue/10 blur-[120px]" aria-hidden />

      <div className="relative max-w-7xl mx-auto">
        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center" data-testid="plug-man-heading">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
            <span className="text-xs uppercase tracking-[0.16em] font-bold" style={{ color: "var(--brand-red-dark)" }}>
              Meet The Mascot
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-brand-navy">
            Meet Plug-Man.<br />
            <span className="text-brand-red">Your local tech hero.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            He doesn't fly. He doesn't shoot lasers. But if your iPhone is cracked, your battery is dying, or your charging port is gone — he's the one you call. Serving Miami Gardens since 2011.
          </p>
        </div>

        {/* Photo grid */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          {/* Tall portrait — left */}
          <div
            className="lg:col-span-5 relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200/60 group"
            data-testid="plug-man-portrait"
          >
            <div className="aspect-[4/5] lg:aspect-[3/5]">
              <img
                src={MEDIA.plugmanPortrait}
                alt="Plug-Man portrait — arms crossed in front of the Your Phone Plug emblem"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/85 via-brand-navy/10 to-transparent" />
            </div>

            {/* Bottom callout */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-red text-white mb-3">
                <Icons.Shield className="w-3.5 h-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Always on duty</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-black text-white leading-tight">
                Cracked screen? Dead battery?<br />Plug-Man's got it covered.
              </h3>
            </div>
          </div>

          {/* Right column */}
          <div className="lg:col-span-7 grid grid-cols-1 gap-5 md:gap-6">
            {/* At the counter */}
            <div
              className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200/60 group bg-brand-navy"
              data-testid="plug-man-counter"
            >
              <div className="aspect-[4/3] sm:aspect-[5/4]">
                <img
                  src={MEDIA.plugmanRepair}
                  alt="Plug-Man working on a phone at the Your Phone Plug repair counter"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  style={{ objectPosition: "center 25%" }}
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-brand-navy/10 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 flex items-end justify-between gap-4">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-300 mb-1.5">In the lab</div>
                  <h4 className="font-display text-xl md:text-2xl font-black text-white leading-tight">Real repairs. Real bench. Real fast.</h4>
                </div>
                <div className="hidden sm:flex items-center gap-2 bg-white/15 border border-white/25 backdrop-blur-md rounded-full px-3 py-1.5 shrink-0">
                  <Icons.Clock className="w-3.5 h-3.5 text-white" />
                  <span className="text-[11px] font-bold text-white">~15 min</span>
                </div>
              </div>
            </div>

            {/* Pointing — call to action */}
            <div
              className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-brand-red/30 group bg-brand-navy"
              data-testid="plug-man-pointing"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="relative aspect-[4/5] sm:aspect-auto">
                  <img
                    src={MEDIA.plugmanPointing}
                    alt="Plug-Man pointing — yes, YOU"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-brand-navy/40 sm:to-brand-navy/80" />
                </div>
                <div className="relative p-6 md:p-8 flex flex-col justify-center">
                  <div className="absolute -left-4 top-1/2 -translate-y-1/2 hidden sm:block">
                    <Icons.ArrowRight className="w-6 h-6 text-brand-red animate-pulse" strokeWidth={3} />
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-red/20 border border-brand-red/40 mb-3 w-fit">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">Yes, YOU</span>
                  </div>
                  <h4 className="font-display text-2xl md:text-3xl font-black text-white tracking-tight leading-tight">
                    Plug-Man is calling.<br /><span className="text-brand-red">Are you ready to fix it?</span>
                  </h4>
                  <p className="text-sm text-white/75 mt-2 max-w-sm">
                    Don't wait. Cracked screens get worse. Bring it in for a free diagnostic today.
                  </p>
                  <div className="mt-5">
                    <CallButton testid="plug-man-call-btn" size="md">Call {BUSINESS.phone}</CallButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
